/**
 * One block on the AOP canvas.
 *
 * The card is a summary: icon and title, a caption, a labelled field block, and
 * one output row per exit down the right edge. Which of those a step shows is
 * the only thing that varies, so the layout stays recognisable however the plan
 * is built.
 *
 * Nothing is edited here. Clicking a card selects it and the panel on the right
 * does the editing — one place to change a step rather than two.
 */

import {
	BranchesOutlined,
	CloseOutlined,
	CustomerServiceOutlined,
	ExclamationCircleFilled,
	FlagFilled,
	FlagOutlined,
	MessageOutlined,
	RightOutlined,
	ShopOutlined,
	ToolOutlined,
	UsergroupAddOutlined,
} from '@ant-design/icons';
import { Popconfirm, Tooltip } from 'antd';
import { violationMessage } from '../utils/AOPViolationUtils';
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import React, { useEffect } from 'react';
import {
	AOPCanvasNodeData,
	AOPDraftStep,
	AOPEscalation,
	AOP_IN_HANDLE,
	AOP_OUT_HANDLE,
} from '../../../types/AOPTypes';
import { useAOPEditor } from '../AOPEditorContext';
import {
	AOP_NODE_BOUNDS_CLASS,
	AOP_OPERATIONS,
	accentTextClassForStep,
	escalationActionLabel,
	escalationStartsAfter,
	formatSecondsLong,
	outcomeDescription,
	outcomeLabel,
	outputsOf,
	partyLabel,
	stepTitle,
	outcomeAccent,
} from '../utils/AOPPlanUtils';

const stepIcon = (step: AOPDraftStep): React.ReactNode => {
	if (step.type === 'unsupported') {
		return <ExclamationCircleFilled />;
	}

	switch (step.type) {
		case 'communicate':
			return <MessageOutlined />;
		case 'operate':
			return <UsergroupAddOutlined />;
		case 'decision':
			return <BranchesOutlined />;
		case 'finish':
			// The flag carries how it ends: a plan is read for where it comes out,
			// and two finish blocks side by side are otherwise indistinguishable
			// until you read their labels.
			return <FlagFilled className={outcomeAccent(step.outcome)} />;
		default:
			return <FlagOutlined />;
	}
};

/* -------------------------------------------------------------------------- */
/*                                  Subviews                                  */
/* -------------------------------------------------------------------------- */

/**
 * The escalation sequence, one icon per step, in the order Radar runs them.
 *
 * The icon follows who is being contacted, not how. Channel would be the
 * natural axis, but it is a const `"note"` upstream, so every escalation that
 * can be saved today is a note and a chain of them drew the same icon N times.
 * `party` does vary, so the chain says something: supplier, then site contact,
 * then the assistant. It moves back to channel once SMS and voice exist.
 */
const partyIcon = (party: string): React.ReactNode => {
	switch (party) {
		case 'buyer':
			return <ShopOutlined />;
		case 'vfa':
			return <CustomerServiceOutlined />;
		case 'supplier':
		default:
			return <ToolOutlined />;
	}
};

const EscalationChain: React.FC<{ timeoutSeconds: number; escalations: AOPEscalation[] }> = ({
	timeoutSeconds,
	escalations,
}) => (
	<div className="flex flex-wrap items-center gap-2xs">
		{escalations.map((escalation, index) => {
			const startsAfter = escalationStartsAfter(timeoutSeconds, escalations, index);

			return (
				<React.Fragment key={index}>
					{index === 0 ? null : (
						/* An icon metric, not type — the type scale stops at Footnote/12. */
						<RightOutlined className="text-[8px] text-character-disabled" />
					)}
					<Tooltip
						overlayStyle={{ maxWidth: 280 }}
						title={
							<div className="flex flex-col gap-2xs">
								<span className="bodymedium">
									{index + 1} · {escalationActionLabel(escalation)}
								</span>
								<span className="footnoteregular">
									Response window: {formatSecondsLong(escalation.window_seconds)}
								</span>
								<span className="footnoteregular">
									{startsAfter > 0
										? `Starts ${formatSecondsLong(
												startsAfter
										  )} after the work order enters this step`
										: 'Starts as soon as the work order enters this step'}
								</span>
							</div>
						}
					>
						<span className="inline-flex h-5 w-5 items-center justify-center rounded-sm bg-purple-1 text-xs leading-none text-purple-6 hover:bg-purple-2">
							{partyIcon(escalation.party)}
						</span>
					</Tooltip>
				</React.Fragment>
			);
		})}
	</div>
);

const Caption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div className="bodyregular text-character-primary">{children}</div>
);

const FieldBlock: React.FC<{ label: string; children: React.ReactNode }> = ({
	label,
	children,
}) => (
	<div className="mt-2xs flex flex-col gap-xxs border-0 border-t border-solid border-grey-4 pt-2xs">
		<span className="footnoteregular uppercase tracking-wider text-grey-6">{label}</span>
		<span className="footnoteregular whitespace-pre-wrap break-words text-character-primary">
			{children}
		</span>
	</div>
);

/* -------------------------------------------------------------------------- */
/*                                  Summaries                                 */
/* -------------------------------------------------------------------------- */

const stepSummary = (step: AOPDraftStep): React.ReactNode => {
	// What the author asked for, in their words. The alternatives are chosen in
	// the panel, where there is room to read them.
	if (step.type === 'unsupported') {
		return <Caption>{step.request}</Caption>;
	}

	if (step.type === 'communicate') {
		return (
			<>
				<Caption>{partyLabel(step.party)}</Caption>
				{/* Labelled for the channel, which is the only one there is today. */}
				<FieldBlock label="Note">{step.message}</FieldBlock>
			</>
		);
	}

	if (step.type === 'operate') {
		// Guarded: `operation` is a vocabulary the service owns and can add to,
		// and an entry we have never seen would otherwise be a crash rather than
		// a block without a caption.
		const operation = AOP_OPERATIONS[step.operation];
		return operation ? <Caption>{operation.description}</Caption> : null;
	}

	if (step.type === 'decision') {
		const escalations = step.escalations ? step.escalations : [];

		// The question is the card's title, so it is not repeated here. Triggers
		// are not drawn either: they say when Radar settles the decision again,
		// which is not something the flow chart shows — the Source tab is where
		// that is read and set.
		return escalations.length > 0 ? (
			<EscalationChain timeoutSeconds={step.timeout_seconds} escalations={escalations} />
		) : null;
	}

	return (
		<Tooltip title={outcomeDescription(step.outcome)}>
			<span className="bodyregular text-character-primary">{outcomeLabel(step.outcome)}</span>
		</Tooltip>
	);
};

/* -------------------------------------------------------------------------- */
/*                                    Node                                    */
/* -------------------------------------------------------------------------- */

interface AOPStepNodeProps {
	id: string;
	data: AOPCanvasNodeData;
	selected?: boolean;
}

export const AOPStepNode: React.FC<AOPStepNodeProps> = ({ id, data, selected }) => {
	const { removeStep, isReadOnly } = useAOPEditor();
	const updateNodeInternals = useUpdateNodeInternals();
	const step = data.step;
	const outputs = outputsOf(step);
	const accentClass = accentTextClassForStep(step.type);
	const summary = stepSummary(step);
	const handleKey = outputs.map((output) => output.id).join('|');

	// React Flow caches handle positions per node; renaming or adding an answer
	// changes the output rows, so it has to re-measure or edges detach.
	useEffect(() => {
		updateNodeInternals(id);
	}, [id, handleKey, updateNodeInternals]);

	// A violation is something the plan gets wrong; a suggestion is something
	// waiting to be ruled on. Both stop a publish, so both earn the badge, but a
	// violation is the louder of the two.
	const openSuggestionCount = data.suggestions.filter(
		(suggestion) => suggestion.status === 'open'
	).length;
	const noteCount = data.violations.length + openSuggestionCount;
	const worstNote =
		data.violations.length > 0 ? 'violation' : openSuggestionCount > 0 ? 'suggestion' : null;

	return (
		// The badge sits on the card's corner, so the positioning context is out
		// here: `.aop-node` clips to its radius and would cut the badge in half.
		// The card is as wide as its content needs, between bounds — layout reads
		// the measured width back, so nothing has to agree on a number. Not
		// `w-max`: `max-content` around a line-clamped title and truncating exit
		// rows is a circular width constraint, and the ResizeObserver never
		// settles.
		<div className={`relative ${AOP_NODE_BOUNDS_CLASS}`}>
			{worstNote ? (
				<Tooltip
					title={
						<div className="flex flex-col gap-2xs">
							{data.violations.map((violation, index) => (
								<span key={`violation-${index}`}>{violationMessage(violation)}</span>
							))}
							{data.suggestions
								.filter((suggestion) => suggestion.status === 'open')
								.map((suggestion, index) => (
									<span key={`suggestion-${index}`}>{suggestion.message}</span>
								))}
						</div>
					}
				>
					<span
						aria-label={`${noteCount} issue${noteCount === 1 ? '' : 's'} on ${stepTitle(step)}`}
						className={`absolute -left-1 -top-1 z-10 h-3 w-3 rounded-full border-2 border-solid border-grey-1 ${
							worstNote === 'violation' ? 'bg-character-danger' : 'bg-purple-6'
						}`}
					/>
				</Tooltip>
			) : null}

			<div
				className="aop-node group w-full"
				data-selected={selected ? '' : undefined}
				data-invalid={data.violations.length > 0 ? '' : undefined}
			>
				{/*
				  Delete lives on the card as well as in the Steps panel and the block
				  panel. It fades in rather than mounting on hover: a Popconfirm whose
				  trigger unmounts when the pointer leaves it closes itself before it
				  can be answered.

				  Quiet until it is the thing being pointed at — grey on the card's
				  corner, danger only once the pointer is on the cross itself. A
				  permanently red button on every block would make a plan look like a
				  list of problems.
				*/}
				{isReadOnly ? null : (
					<Popconfirm
						title="Delete this block? Anything pointing at it will need rewiring."
						okText="Delete"
						okButtonProps={{ danger: true }}
						cancelText="Cancel"
						onConfirm={() => removeStep(step.name)}
					>
						<button
							type="button"
							aria-label={`Delete ${stepTitle(step)}`}
							onClick={(event) => event.stopPropagation()}
							className="nodrag nopan absolute -right-2 -top-2 z-10 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-solid border-grey-4 bg-grey-1 p-0 text-character-secondary opacity-0 shadow-sm transition-opacity hover:border-character-danger hover:text-character-danger focus:opacity-100 group-hover:opacity-100"
						>
							<CloseOutlined style={{ fontSize: 10 }} />
						</button>
					</Popconfirm>
				)}
				<Handle type="target" position={Position.Left} id={AOP_IN_HANDLE} className="aop-handle" />

				<div className="flex items-start gap-xs border-0 border-b border-solid border-grey-3 px-s py-xs">
					<span
						className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border border-solid border-grey-5 bg-grey-1 ${accentClass}`}
					>
						{stepIcon(step)}
					</span>
					<div className="min-w-0 flex-1">
						{/* Two lines, then an ellipsis — a long question is the title here. */}
						<span className="bodymedium line-clamp-2 block text-character-primary">
							{stepTitle(step)}
						</span>
					</div>
					{/*
					  The number stays on hover. It used to fade out to make room for the
					  delete cross, but the cross sits outside the card's corner and never
					  overlapped it — so all the fade did was take away where you are in
					  the plan at the moment you reached for something.
					*/}
					<span className="footnoteregular shrink-0 text-character-secondary">
						{data.stepNumber}
					</span>
				</div>

				{summary ? (
					<div className="px-s py-xs">
						<div className="flex flex-col gap-2xs">{summary}</div>
					</div>
				) : null}

				{/* The band rounds its own bottom corners: the card no longer clips. */}
				{outputs.length > 0 ? (
					<div className="rounded-b border-0 border-t border-solid border-grey-3 bg-purple-1 py-0.5">
						{outputs.map((output) => (
							<div
								key={output.id}
								className="relative flex h-[22px] items-center justify-end pr-2.5"
								title={output.title}
							>
								<span className="footnoteregular truncate">
									<span className="text-character-disabled">
										{output.leading ? output.leading : 'if'} {output.leading === 'If' ? null : '· '}
									</span>
									{output.condition ? (
										<span className="text-character-disabled">{output.condition} </span>
									) : null}
									<span className="text-purple-6">{output.label}</span>
									{output.suffix ? (
										<span className="text-character-disabled"> {output.suffix}</span>
									) : null}
								</span>
								<Handle
									type="source"
									position={Position.Right}
									id={output.id}
									className="aop-handle"
								/>
							</div>
						))}
					</div>
				) : null}

				{/*
				  Nothing here any more: every step that continues says so through an
				  exit, including a communicate step's `after`, so each one has its own
				  handle in the rows above. The lone dot existed for the step that had
				  no exit to draw and continued by list order instead.
				*/}
			</div>
		</div>
	);
};

export const aopNodeTypes = { aopStep: AOPStepNode };
